import { z } from 'zod';

import { ModelIdSchema } from '../model-tiers';

// A transcript message mirrors PydanticAI's pydantic_ai.messages — a ModelMessage
// is a request or a response, each an ordered list of parts discriminated on
// partKind. Literal kind/partKind values are kept identical to PydanticAI so
// serialized history stays interchangeable; the names are camelCased to our style.

// PydanticAI ModelResponse.finish_reason, trimmed to what Gemini reports.
export const FINISH_REASONS = ['stop', 'length', 'content_filter', 'tool_call', 'error'] as const;
export const FinishReasonSchema = z.enum(FINISH_REASONS);
export type FinishReason = z.infer<typeof FinishReasonSchema>;

// --- Request parts (fed into the model) ---

// system-prompt — the agent's system prompt for this turn (from the AgentVersion).
const SystemPromptPartSchema = z.object({
  partKind: z.literal('system-prompt'),
  content: z.string(),
});

// user-prompt — operator/end-user input. Phase 3: text only.
// Growth path: content -> z.union([z.string(), z.array(UserContentSchema)]) for multimodal.
const UserPromptPartSchema = z.object({
  partKind: z.literal('user-prompt'),
  content: z.string().min(1),
});

// tool-return — the result of a tool call, correlated by toolCallId.
const ToolReturnPartSchema = z.object({
  partKind: z.literal('tool-return'),
  toolName: z.string(),
  toolCallId: z.string(),
  content: z.json(), // PydanticAI ToolReturnContent: string | object | array
});

// retry-prompt — the loop asks the model to retry a malformed/failed tool call.
const RetryPromptPartSchema = z.object({
  partKind: z.literal('retry-prompt'),
  content: z.string(), // PydanticAI also allows ErrorDetails[]; we stringify for Phase 3
  toolName: z.string().nullable().default(null),
  toolCallId: z.string().nullable().default(null),
});

export const RequestPartSchema = z.discriminatedUnion('partKind', [
  SystemPromptPartSchema,
  UserPromptPartSchema,
  ToolReturnPartSchema,
  RetryPromptPartSchema,
]);
export type RequestPart = z.infer<typeof RequestPartSchema>;

// --- Response parts (emitted by the model) ---

// text — assistant prose (the chat bubble).
const TextPartSchema = z.object({
  partKind: z.literal('text'),
  content: z.string(),
});

// thinking — reasoning trace. signature is the provider's opaque verifier token.
const ThinkingPartSchema = z.object({
  partKind: z.literal('thinking'),
  content: z.string(),
  signature: z.string().nullable().default(null),
});

// tool-call — the model asks to run a tool. args may arrive as raw JSON text OR a
// parsed object (PydanticAI: str | dict | None) — accept both. signature is the
// provider's opaque verifier token (Gemini's thoughtSignature); it rides on the
// tool-call part and must be echoed back on replay or Gemini 3 rejects the turn.
const ToolCallPartSchema = z.object({
  partKind: z.literal('tool-call'),
  toolName: z.string(),
  toolCallId: z.string(),
  args: z
    .union([z.string(), z.record(z.string(), z.unknown())])
    .nullable()
    .default(null),
  signature: z.string().nullable().default(null),
});

export const ResponsePartSchema = z.discriminatedUnion('partKind', [
  TextPartSchema,
  ThinkingPartSchema,
  ToolCallPartSchema,
]);
export type ResponsePart = z.infer<typeof ResponsePartSchema>;

// --- Usage (tokens) ---

// PydanticAI RequestUsage, trimmed. total is derivable, so not stored.
export const UsageSchema = z.object({
  inputTokens: z.number().int().nonnegative().default(0),
  outputTokens: z.number().int().nonnegative().default(0),
  cacheReadTokens: z.number().int().nonnegative().default(0),
  cacheWriteTokens: z.number().int().nonnegative().default(0),
});
export type Usage = z.infer<typeof UsageSchema>;

// --- Message ---

// A message is one request or response, discriminated on kind. The persisted shape
// adds the envelope (id, sessionId, seq, createdAt); responses additionally carry
// usage, modelName, finishReason, and durationMs.
const MessageEnvelopeShape = {
  id: z.uuid(),
  sessionId: z.uuid(),
  seq: z.number().int().nonnegative(), // monotonic per session, defines transcript order
  createdAt: z.iso.datetime(),
};

const RequestMessageSchema = z.object({
  kind: z.literal('request'),
  parts: z.array(RequestPartSchema).min(1),
  ...MessageEnvelopeShape,
});

const ResponseMessageSchema = z.object({
  kind: z.literal('response'),
  parts: z.array(ResponsePartSchema),
  usage: UsageSchema,
  modelName: ModelIdSchema,
  finishReason: FinishReasonSchema.nullable().default(null),
  durationMs: z.number().int().nonnegative().nullable().default(null), // model-call latency
  ...MessageEnvelopeShape,
});

export const MessageSchema = z.discriminatedUnion('kind', [
  RequestMessageSchema,
  ResponseMessageSchema,
]);
export type Message = z.infer<typeof MessageSchema>;
